import { useState } from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleNotch, faPaperclip, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import toast from 'react-hot-toast';
import {
  INVOICE_CHECK_LABELS,
  describeInvoiceIssue,
  type InvoiceCheckIssue,
  type InvoiceCheckStatus,
  type PeriodSelection,
} from '@salaries/shared';
import { fetchReceiptObjectUrl } from '../../api/payroll';

// Invoice link — clicking the icon opens the חשבונית directly in a new tab.
// A url invoice opens its link; an uploaded file is fetched (auth'd) and opened.
// The blank tab is opened synchronously on the click so popup-blockers don't
// swallow it (mirrors openArboxProfile).
export function ReceiptPreview({
  staffId,
  unitId,
  selection,
  invoiceUrl,
  checkStatus,
  checkIssues,
}: {
  staffId: number;
  /** The row's P&L unit — each unit line carries its own invoice. */
  unitId?: number;
  selection: PeriodSelection;
  invoiceUrl: string | null;
  /** Overall verdict of the AI invoice check; null when the box doesn't check. */
  checkStatus: InvoiceCheckStatus | null;
  /** Which checks failed — spelled out in the tooltip. */
  checkIssues: InvoiceCheckIssue[];
}) {
  const [loading, setLoading] = useState(false);

  const open = async () => {
    if (invoiceUrl) {
      window.open(invoiceUrl, '_blank', 'noopener');
      return;
    }
    const tab = window.open('', '_blank');
    setLoading(true);
    try {
      const { url } = await fetchReceiptObjectUrl(staffId, selection, unitId);
      if (tab) tab.location.href = url;
      else window.open(url, '_blank');
    } catch {
      tab?.close();
      toast.error('שגיאה בטעינת החשבונית');
    } finally {
      setLoading(false);
    }
  };

  // The paperclip carries the invoice-check verdict in its colour, so a mismatch
  // is visible while scanning the column — the operator shouldn't have to open
  // every invoice to find the one that's wrong. No check → the usual green.
  const tone =
    checkStatus === 'mismatch'
      ? 'text-error-red hover:text-error-red/80'
      : checkStatus === 'unreadable'
        ? 'text-secondary-text hover:text-main-text'
        : 'text-success-green hover:text-success-green/80';

  const title =
    checkStatus === 'mismatch'
      ? `${INVOICE_CHECK_LABELS.mismatch}: ${checkIssues.map(describeInvoiceIssue).join(' ')}`
      : checkStatus === 'unreadable'
        ? `${INVOICE_CHECK_LABELS.unreadable} — פתח חשבונית`
        : checkStatus === 'match'
          ? `${INVOICE_CHECK_LABELS.match} — פתח חשבונית`
          : 'פתח חשבונית';

  return (
    <Tooltip title={title}>
      <button type="button" onClick={open} aria-label={title} className={`${tone} cursor-pointer`}>
        <FontAwesomeIcon
          icon={loading ? faCircleNotch : checkStatus === 'mismatch' ? faTriangleExclamation : faPaperclip}
          spin={loading}
        />
      </button>
    </Tooltip>
  );
}
